<template>
  <span
    class="bg-static-white flex items-center justify-center overflow-hidden rounded-full border"
    :style="`border-color: ${airlineColor}`"
    :class="iconSize"
  >
    <img
      :src="airlineIconQuery"
      :alt="airline.title"
      :width="airlineIcon.renderSize"
      :height="airlineIcon.renderSize"
      data-test-id="airline-icon-img"
    />
  </span>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import { AirlineIconSizeEnum } from '../types';
  import type { Airline } from '../types';

  const props = withDefaults(
    defineProps<{
      airline: Airline;
      airlineIcon?: boolean;
      size?: AirlineIconSizeEnum;
    }>(),
    { size: AirlineIconSizeEnum.default },
  );

  const airlineColor = computed(() => {
    switch (props.airline.iata) {
      case 'KC-':
      case 'FS':
        return '#B2223A';
      case 'KC':
        return '#9C8761';
      case 'DV':
        return '#00569F';
      case 'IQ':
        return '#9C158F';
      default:
        return '#B9B9B9';
    }
  });

  const iconSize = [
    { 'size-4': props.size === AirlineIconSizeEnum.small },
    { 'size-5': props.size === AirlineIconSizeEnum.medium },
    { 'size-6': props.size === AirlineIconSizeEnum.default },
    { 'size-8': props.size === AirlineIconSizeEnum.large },
  ];

  const airlineIcon = (function getIconSize() {
    switch (props.size) {
      case AirlineIconSizeEnum.small:
        // Keep the image inside the size-4 (16px) circle: container size
        // minus the 1px borders, matching medium (20 -> 18), default
        // (24 -> 22) and large (32 -> 30). 22px overflowed the circle
        // and cropped the logo.
        return {
          renderSize: 22,
          querySize: '80x80',
        };
      case AirlineIconSizeEnum.medium:
        return {
          renderSize: 18,
          querySize: '80x80',
        };
      case AirlineIconSizeEnum.default:
        return {
          renderSize: 22,
          querySize: '80x80',
        };
      case AirlineIconSizeEnum.large:
        return {
          renderSize: 30,
          querySize: '80x80',
        };
    }
  })();

  const STATIC_BASE_URL =
    import.meta.env.VITE_RUNTIME_ENV === 'production'
      ? 'https://aviata.kz/static'
      : 'https://aviax-freedom-travel.dev.cloud.aviata.team/static';

  const airlineIconQuery = computed(() => {
    return `${STATIC_BASE_URL}/airline-logos/${airlineIcon.querySize}/${props.airline.iata}.png`;
  });
</script>
